// =============================================================================
// AuthBackdrop.js — Coque visuelle COMMUNE aux écrans d'authentification
// =============================================================================
// Partagée par la page de garde (PageGarde, dans App.jsx) ET par tous les
// écrans de connexion (AramisLogin.js : login, inscription, changePassword,
// totp, totpSetup, passkey) → aucune rupture visuelle d'un écran à l'autre.
//
// Composition : fond dégradé navy Enio + pictogramme losanges GÉANT en
// filigrane (la marque habille le fond) + halos flottants aux couleurs vives
// du logo. Le fond reste sombre dans les deux thèmes ; la carte de connexion,
// elle, suit le thème via var(--bg-card).
//
// `prefers-reduced-motion` désactive toutes les animations.
// =============================================================================
import { useState, useEffect, useRef, useCallback } from "react";
import { EnioLogo } from "./appShared";
import { IconeTexte } from "./icones";
import { RAYON_COINS } from "./PictoEnioAnime";

export const AuthBackdrop = ({ children }) => (
  <div className="auth-backdrop" style={{display:"flex",alignItems:"center",justifyContent:"center",
    position:"relative",overflow:"hidden",
    background:"radial-gradient(1100px 700px at 15% 0%, #0d4260 0%, #082536 48%, #04121a 100%)"}}>
    <style>{`
      /* Hauteur et zone sure de la coque d'authentification.
         100vh vaut sur iOS la hauteur BARRES MASQUEES : le centrage vertical
         tombait ~50px trop bas et le bouton d'action de la carte finissait
         derriere la barre d'outils Safari. 100dvh suit les barres reellement
         affichees (le 100vh reste en repli pour les vieux navigateurs).
         Le rembourrage s'indexe sur la zone sure : sans lui, en paysage ou en
         PWA installee, la carte passe sous l'encoche et la barre d'accueil. */
      .auth-backdrop{
        min-height:var(--vh100);
        padding:max(24px, env(safe-area-inset-top,0px)) max(24px, env(safe-area-inset-right,0px)) max(24px, env(safe-area-inset-bottom,0px)) max(24px, env(safe-area-inset-left,0px));
      }
      @keyframes authFloat1 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(46px,-34px) scale(1.10)} }
      @keyframes authFloat2 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(-34px,44px) scale(1.14)} }
      @keyframes authFloat3 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(24px,24px) scale(.94)} }
      @keyframes authDrift  { 0%,100%{transform:rotate(-3deg) scale(1)} 50%{transform:rotate(3deg) scale(1.05)} }
      @keyframes authIn     { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:none} }
      .auth-blob{ position:absolute;border-radius:50%;filter:blur(70px);pointer-events:none; }
      .auth-in{ animation:authIn .55s cubic-bezier(.2,.7,.3,1) both; }
      .auth-cta{ transition:transform .18s ease, box-shadow .18s ease, filter .18s ease; }
      /* Survol réservé à la souris (il restait collé au doigt), sans halo
         coloré ; appui à 97 % (lot L7). */
      @media (hover:hover) and (pointer:fine){
        .auth-cta:hover{ transform:translateY(-2px); box-shadow:0 12px 28px rgba(0,0,0,.28); }
      }
      .auth-cta:active{ transform:scale(var(--motion-press, .97)); }
      @media (prefers-reduced-motion: reduce){
        .auth-blob,.auth-mark,.auth-in,.auth-cta{ animation:none !important; transition:none !important; }
      }
    `}</style>

    {/* Halos flottants — couleurs vives du logo Enio */}
    <div className="auth-blob" style={{top:-110,left:-90,width:380,height:380,opacity:.34,
      background:"radial-gradient(circle,#F49800,transparent 70%)",animation:"authFloat1 15s ease-in-out infinite"}}/>
    <div className="auth-blob" style={{bottom:-130,right:-90,width:440,height:440,opacity:.30,
      background:"radial-gradient(circle,#1D948A,transparent 70%)",animation:"authFloat2 19s ease-in-out infinite"}}/>
    <div className="auth-blob" style={{top:"38%",left:"58%",width:320,height:320,opacity:.20,
      background:"radial-gradient(circle,#2AA9E1,transparent 70%)",animation:"authFloat3 23s ease-in-out infinite"}}/>

    {/* Pictogramme losanges GÉANT — la marque en fond, qui respire lentement */}
    <img src="/enio-losanges-gris.svg" alt="" aria-hidden="true" className="auth-mark"
      style={{position:"absolute",right:"calc(var(--vw100) * -0.08)",bottom:"calc(var(--vh100) * -0.16)",width:"min(var(--vw100) * 0.8,880px)",
        opacity:.085,pointerEvents:"none",userSelect:"none",
        animation:"authDrift 26s ease-in-out infinite"}}/>

    <div style={{position:"relative",zIndex:1,width:"100%",display:"flex",justifyContent:"center"}}>
      {children}
    </div>
  </div>
);

// Disque « verre dépoli » qui met le logo Enio en valeur sur le fond sombre.
export const AuthLogoDisc = ({ size = 84 }) => (
  <div style={{width:size+48,height:size+48,borderRadius:"50%",margin:"0 auto",
    display:"flex",alignItems:"center",justifyContent:"center",
    background:"rgba(255,255,255,.06)",border:"1px solid rgba(255,255,255,.14)",
    boxShadow:"0 18px 60px rgba(0,0,0,.45), inset 0 1px 0 rgba(255,255,255,.18)"}}>
    <EnioLogo size={size}/>
  </div>
);

// ── Écran de démarrage : LA GENÈSE ───────────────────────────────────────────
// Affiché pendant le chargement initial des données (App.jsx, porte
// loading||config===null). Le pictogramme se CONSTRUIT pièce à pièce :
// chaque losange se condense depuis la poussière lumineuse (arrive de
// l'extérieur, flou, éclat à la naissance), le picto tient et respire, puis
// se dissout en cascade et renaît — boucle de 7 s tant que ça charge. Quand
// les données arrivent (prop `exiting`), les pièces s'assemblent d'un coup
// (snap 0,3 s) et l'écran s'efface en fondu — le big bang reste réservé au
// déverrouillage post-connexion (pas de redite, choix Pierre).
// BOOT_EPOCH ancre la phase des animations : sur window.__BOOT_T0 si le
// splash statique d'index.html a démarré la boucle avant nous (React reprend
// alors EXACTEMENT où en est l'animation, via délais négatifs), sinon au
// chargement du module — le remontage éventuel du composant (bascule vers la
// sortie) reprend de même la boucle sans redémarrage visible.
const BOOT_EPOCH = (typeof window !== "undefined" && window.__BOOT_T0) || Date.now();
export const BootGenese = ({ exiting = false }) => {
  const phase0 = useRef(-((Date.now() - BOOT_EPOCH) / 1000)).current;
  return (
    <div style={{position:"fixed",inset:0,zIndex:99990,overflow:"hidden",
      display:"flex",alignItems:"center",justifyContent:"center",
      background:"radial-gradient(1100px 700px at 15% 0%, #0d4260 0%, #082536 48%, #04121a 100%)",
      opacity:exiting?0:1,transition:"opacity .45s ease .15s",
      pointerEvents:exiting?"none":"auto"}}>
      <style>{`
        @keyframes bootPiece{
          0%,2%{opacity:0;transform:translate(var(--mx),var(--my)) rotate(45deg) scale(.5);filter:blur(6px);box-shadow:0 0 0 rgba(255,244,214,0)}
          7%{opacity:1;transform:translate(0,0) rotate(45deg) scale(1.14);filter:blur(0px);box-shadow:0 0 22px rgba(255,244,214,.45)}
          11%{box-shadow:0 0 0 rgba(255,244,214,0)}
          40%{transform:translate(0,0) rotate(45deg) scale(1.03)}
          72%{opacity:1;transform:translate(0,0) rotate(45deg) scale(1);filter:blur(0px)}
          84%,100%{opacity:0;transform:translate(calc(var(--mx)*-0.6),calc(var(--my)*-0.6)) rotate(45deg) scale(.7);filter:blur(5px)}
        }
        @keyframes bootShine{from{background-position:200% 0}to{background-position:-100% 0}}
        @keyframes bootFadeIn{from{opacity:0}to{opacity:1}}
        @media (prefers-reduced-motion: reduce){
          .boot-piece{animation:none !important;opacity:1 !important;transform:translate(0,0) rotate(45deg) scale(1) !important;filter:none !important}
          .boot-title{animation:none !important}
        }
        .boot-mark{width:220px;height:220px}
        /* Ecran de demarrage en PAYSAGE sur iPhone : le bloc fait ~360px de
           haut (220px de losanges + titre + phrase) dans un calque fixe
           inset:0 overflow:hidden — le haut du picto et le bas de la phrase
           etaient rognes, sans defilement possible pour aller les chercher.
           On retrecit le pictogramme (les losanges sont places en pixels
           absolus autour du centre : reduire height/width les deformerait,
           seul un scale conserve la geometrie officielle).
           La boite est retrecie EN MEME TEMPS que le scale (meme facteur) :
           sans cela le bloc garderait ses 220px de haut dans le flux et on
           n'aurait rien regagne.
           Ces trois paliers sont conditionnes au pointeur grossier : un
           ordinateur portable a souvent moins de 700px de hauteur visible et
           doit garder le pictogramme de 220px d'origine. */
        @media (pointer:coarse) and (max-height:700px){ .boot-mark{width:154px;height:154px;transform:scale(.7)} .boot-wrap{padding:16px 24px} }
        @media (pointer:coarse) and (max-height:560px){ .boot-mark{width:110px;height:110px;transform:scale(.5)} }
        @media (pointer:coarse) and (max-height:440px){ .boot-mark{width:79px;height:79px;transform:scale(.36)} }
      `}</style>
      {/* Losanges en filigrane, cohérent avec AuthBackdrop qui suit */}
      <img src="/enio-losanges-gris.svg" alt="" aria-hidden="true"
        style={{position:"absolute",right:"calc(var(--vw100) * -0.08)",bottom:"calc(var(--vh100) * -0.16)",width:"min(var(--vw100) * 0.8,880px)",
          opacity:.05,pointerEvents:"none"}}/>
      <div className="boot-wrap" style={{textAlign:"center",maxWidth:420,padding:24}}>
        <div className="boot-mark" style={{position:"relative",margin:"0 auto"}}>
          <div style={{position:"absolute",left:"50%",top:"50%",width:0,height:0,isolation:"isolate"}}>
            {UNLOCK_PIECES.map((p,i)=>{
              const [x,y,s,col]=p;
              const st={position:"absolute",left:x-s/2,top:y-s/2,width:s,height:s,
                background:col,borderRadius:rayonPiece(s),mixBlendMode:"multiply",
                "--mx":(x*0.38).toFixed(1)+"px","--my":(y*0.38).toFixed(1)+"px"};
              if(exiting) return <div key={i} className="boot-piece" style={{...st,opacity:1,
                transform:"translate(0,0) rotate(45deg) scale(1)",filter:"none",
                transition:"transform .3s cubic-bezier(.3,1.3,.4,1), opacity .25s ease, filter .25s ease"}}/>;
              return <div key={i} className="boot-piece" style={{...st,opacity:0,
                animation:`bootPiece 7s ease ${(phase0+i*0.12).toFixed(2)}s infinite`}}/>;
            })}
          </div>
        </div>
        <div className="boot-title" style={{fontSize:"var(--fs-section)",fontWeight:300,letterSpacing:"calc(.14em * var(--em-ajuste, 1))",
          fontFamily:"Montserrat,sans-serif",marginTop:6,
          background:"linear-gradient(100deg, rgba(255,255,255,.95) 30%, #F49800 50%, rgba(255,255,255,.95) 70%)",
          backgroundSize:"250% 100%",WebkitBackgroundClip:"text",backgroundClip:"text",
          WebkitTextFillColor:"transparent",
          animation:"bootShine 3.2s ease-in-out infinite"}}>
          aramis
        </div>
        <div style={{fontSize:"var(--fs-nav)",color:"#8aabb8",fontFamily:"Montserrat,sans-serif",lineHeight:1.6,
          marginTop:12,animation:"bootFadeIn .8s ease .3s both"}}>
          Bienvenue sur Aramis, l'espace dédié aux mutuelles d'Enio.
        </div>
      </div>
    </div>
  );
};

// ── Animation de déverrouillage (post-connexion) : BIG BANG ──────────────────
// Les 17 losanges arrivent de PARTOUT hors de l'écran (directions en angle
// d'or → toutes les bordures) À LEUR TAILLE RÉELLE, rétrécissent en volant
// vers le centre et se FONDENT en un seul petit losange (échelle de fusion
// propre à chaque pièce → superposition exacte, même point même taille).
// Le point unique VIBRE — la tension monte, la lumière grandit derrière —
// flash — big bang — et l'explosion projette chaque losange exactement à sa
// place : le logo Enio naît de la déflagration. Puis amarrage vers le haut
// pendant que l'app se révèle.
// Joue UNE fois par-dessus l'écran après la validation complète du login —
// purement visuel : la session est écrite AVANT le lancement (aucune logique
// d'authentification ici, seul le navTo final est différé).
// Clic n'importe où = passer ; prefers-reduced-motion = bascule immédiate.
// Pièces = géométrie EXACTE du pictogramme officiel (PICTO ENIO.svg) : centres,
// côtés et couleurs extraits des 17 <rect rotate(135)> du fichier Illustrator
// (normalisés, échelle 0.21). Ordre d'empilement : celui de LOSANGES
// (src/PictoEnioAnime.js), le grand losange ROUGE au premier plan, dessiné en
// dernier, comme sur le logo officiel (recette du 18/10/2026 : le beige
// passait devant ; verrou src/pictoOrdre.test.js). Les chevauchements
// s'assombrissent comme l'original (mix-blend multiply isolé).
export const UNLOCK_PIECES = [
  [-3,68,36,"#95C11F"],[30,-34,21,"#95C11F"],[54,1,39,"#02938A"],[-23,44,15,"#36A9E1"],
  [2,-28,45,"#CBBBA0"],[-80,1,23,"#CBBBA0"],[-22,11,39,"#F49800"],[-2,-68,34,"#36A9E1"],
  [80,1,15,"#E20A17"],[-40,-34,30,"#D60B52"],[36,34,22,"#EC6608"],[-44,22,32,"#951B81"],
  [30,-48,17,"#951B81"],[-61,-5,21,"#02938A"],[11,57,13,"#02938A"],[15,36,28,"#E71D73"],
  [9,-4,44,"#E20A17"],
];
// Coin d'une pièce, en pourcentage de SON côté : RAYON_COINS à l'échelle
// 0.21 des pièces, donc le même rayon absolu pour les 17 (le logo actuel),
// et juste à toute taille d'affichage (salle de vote comprise).
export const rayonPiece = (s) => `${(RAYON_COINS * 0.21 / s * 100).toFixed(2)}%`;
// Trajectoires précalculées (déterministes) : direction d'arrivée en angle
// d'or (les 17 losanges couvrent toutes les bordures de l'écran) et vrille
// de vol. FUSION_PX = côté du losange unique dans lequel toutes les pièces
// se fondent au centre : chaque pièce a sa propre échelle de fusion
// (FUSION_PX / son côté) pour que les 17 finissent EXACTEMENT superposées,
// même taille même point — un seul losange à l'écran.
const FUSION_PX = 13;
const UNLOCK_FLIGHT = UNLOCK_PIECES.map((p, i) => {
  const a = i * 2.399963 + 0.7;
  return {
    sx: +(Math.cos(a) * 62).toFixed(1),          // départ hors écran (centièmes du plus grand côté)
    sy: +(Math.sin(a) * 62).toFixed(1),
    spin: (i % 2 ? -1 : 1) * (200 + i * 21),     // vrille pendant le vol
    fuse: +(FUSION_PX / p[2]).toFixed(3),        // échelle au point de fusion
  };
});
export const UnlockAnimation = ({prenom="", onDone, slow=false}) => {
  const [phase,setPhase] = useState(0); // 0 dispersé → 1 fusion → 2 vibration → 3 big bang → 4 amarrage
  const doneRef = useRef(false);
  // onDone vit dans une ref : le parent (App) le recrée à chaque render, et il
  // ne doit SURTOUT pas réarmer la timeline (sinon les phases sont repoussées
  // à l'infini par les re-renders — bug observé). La timeline ne s'arme qu'au
  // montage ; le rythme (slow = démo ?unlockdemo=1) est figé au montage.
  const onDoneRef = useRef(onDone); onDoneRef.current = onDone;
  const kRef = useRef(slow ? 1.5 : 1);
  const k = kRef.current;
  // Rythme (k=1, ~4,55 s) — chaque acte se termine AVANT le suivant :
  //   0,06 s  fusion — les losanges entrent à taille réelle et RÉTRÉCISSENT
  //           en volant vers le centre, où ils se fondent en UN SEUL petit
  //           losange (vol 1,0 s, cascade 20 ms → fusion complète à ~1,4 s)
  //   1,50 s  vibration — le point unique tremble, la lumière monte derrière
  //   2,15 s  BIG BANG — flash + ondes de choc, l'explosion forme le logo (~3,2 s)
  //   3,45 s  amarrage — « Bienvenue » pendant que l'app se révèle
  //   4,55 s  sortie
  const D = {
    fly:     1.0*k,         // vol + rétrécissement depuis les bords (s)
    shake:   0.3*k,         // un cycle de vibration du point fusionné (s)
    burst:   0.85*k,        // explosion vers la forme du logo (s)
    dock:    0.80*k,        // amarrage vers le haut (s)
  };
  const finish = useCallback(()=>{
    if(doneRef.current) return; doneRef.current = true;
    if(onDoneRef.current) onDoneRef.current();
  },[]);
  useEffect(()=>{
    if(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches){ finish(); return; }
    const kk = kRef.current;
    const t = [
      setTimeout(()=>setPhase(1),   60),
      setTimeout(()=>setPhase(2), 1500*kk),
      setTimeout(()=>setPhase(3), 2150*kk),
      setTimeout(()=>setPhase(4), 3450*kk),
      setTimeout(finish,          4550*kk),
    ];
    return ()=>t.forEach(clearTimeout);
  },[finish]);
  // Style d'un losange selon l'acte. Le translate(-x,-y) ramène le CENTRE de
  // la pièce EXACTEMENT au centre de l'écran (fusion), translate(0,0) = sa
  // place dans le logo. L'échelle de fusion est propre à chaque pièce (fuse)
  // pour que toutes se superposent au même point à la même taille : un seul
  // losange visible. Entrée à taille réelle (scale 1), rétrécissement en vol.
  const pieceStyle = (p,i) => {
    const [x,y,s,col] = p; const f = UNLOCK_FLIGHT[i];
    const fused = `translate(${-x}px,${-y}px) rotate(45deg) scale(${f.fuse})`;
    const base = {position:"absolute",left:x-s/2,top:y-s/2,width:s,height:s,
      background:col,borderRadius:rayonPiece(s),mixBlendMode:"multiply",
      willChange:"transform,opacity,filter"};
    if(phase===0) return {...base,opacity:0,
      transform:`translate(calc(max(var(--vw100), var(--vh100)) * ${(f.sx / 100).toFixed(3)}),calc(max(var(--vw100), var(--vh100)) * ${(f.sy / 100).toFixed(3)})) rotate(${f.spin}deg) scale(1)`,
      filter:"blur(3px)"};
    if(phase===1) return {...base,opacity:1,
      transform:fused,
      filter:"blur(.6px)",
      transition:`transform ${D.fly}s cubic-bezier(.5,.04,.74,.24) ${i*20*k}ms, opacity ${D.fly*0.3}s ease ${i*20*k}ms, filter ${D.fly}s ease ${i*20*k}ms`};
    if(phase===2) return {...base,opacity:1,
      transform:fused,
      filter:"blur(.6px)"};
    return {...base,opacity:1,
      transform:"translate(0,0) rotate(45deg) scale(1)",
      filter:"blur(0) drop-shadow(1px 1px 2px rgba(30,30,28,.3))",
      transition:`transform ${D.burst}s cubic-bezier(.18,1.45,.32,1) ${i*12*k}ms, filter ${D.burst*0.7}s ease ${i*12*k}ms`};
  };
  // Lumière centrale : PREMIER enfant du groupe isolé, donc DERRIÈRE la boule
  // — la matière comprimée (multiply) s'éclaire par transparence, puis la
  // lumière explose (bbFlare) au moment du big bang.
  const glowStyle = (() => {
    const base = {position:"absolute",left:-120,top:-120,width:240,height:240,borderRadius:"50%",
      pointerEvents:"none",
      background:"radial-gradient(circle, #FFF7E6 0%, rgba(255,214,140,.85) 30%, rgba(244,152,0,.35) 55%, transparent 72%)"};
    if(phase<=0) return {...base,opacity:0,transform:"scale(.3)"};
    if(phase===1) return {...base,opacity:.3,transform:"scale(.55)",
      transition:`opacity ${D.fly}s ease ${0.25*k}s, transform ${D.fly}s ease ${0.25*k}s`};
    if(phase===2) return {...base,opacity:1,transform:"scale(1)",
      transition:`opacity ${0.5*k}s ease-in, transform ${0.5*k}s ease-in`};
    return {...base,opacity:0,animation:`bbFlare ${0.8*k}s cubic-bezier(.16,.84,.3,1) forwards`};
  })();
  return(
    <div onClick={finish} title="Cliquer pour passer"
      style={{position:"fixed",inset:0,zIndex:100000,cursor:"pointer",overflow:"hidden",
        background:"radial-gradient(1100px 700px at 15% 0%, #0d4260 0%, #082536 48%, #04121a 100%)"}}>
      <style>{`
        @keyframes unlockWave{0%{opacity:.85;transform:scale(.5)}100%{opacity:0;transform:scale(9)}}
        @keyframes bbFlare{0%{opacity:1;transform:scale(1)}100%{opacity:0;transform:scale(5.5)}}
        @keyframes bbFlash{0%{opacity:0}12%{opacity:.92}100%{opacity:0}}
        @keyframes bbShake{
          0%,100%{transform:translate(0,0) scale(1)}
          18%{transform:translate(2.5px,-2px) scale(1.05)}
          38%{transform:translate(-2.5px,1.5px) scale(.96)}
          58%{transform:translate(2px,2.5px) scale(1.06)}
          78%{transform:translate(-1.5px,-2.5px) scale(.97)}
        }
      `}</style>
      {/* Losanges en filigrane, cohérent avec AuthBackdrop */}
      <img src="/enio-losanges-gris.svg" alt="" aria-hidden="true"
        style={{position:"absolute",right:"calc(var(--vw100) * -0.08)",bottom:"calc(var(--vh100) * -0.16)",width:"min(var(--vw100) * 0.8,880px)",
          opacity:.06,pointerEvents:"none"}}/>
      <div style={{position:"absolute",left:"50%",top:"46%",width:0,height:0,
        transform:phase>=4?"translateY(calc(var(--vh100) * -0.3)) scale(.4)":"none",
        transition:`transform ${D.dock}s cubic-bezier(.3,.9,.3,1)`}}>
        {/* La vibration secoue le groupe entier (matière + lumière) : le point
            fusionné tremble de plus en plus fort juste avant le big bang */}
        <div style={{isolation:"isolate",
          animation:phase===2?`bbShake ${D.shake}s linear infinite`:"none"}}>
          <div style={glowStyle}/>
          {UNLOCK_PIECES.map((p,i)=>(<div key={i} style={pieceStyle(p,i)}/>))}
        </div>
        {/* Ondes de choc du big bang */}
        {phase>=3 && [0,1,2].map(k2=>(
          <div key={k2} style={{position:"absolute",left:-20,top:-20,width:40,height:40,borderRadius:"50%",
            pointerEvents:"none",
            border:`2px solid ${["rgba(244,152,0,.7)","rgba(42,169,225,.6)","rgba(148,193,31,.55)"][k2]}`,
            animation:`unlockWave ${1.2*k}s ease-out ${k2*0.15*k}s forwards`}}/>
        ))}
      </div>
      {/* Flash plein écran du big bang */}
      {phase>=3 && (
        <div style={{position:"fixed",inset:0,pointerEvents:"none",
          background:"radial-gradient(circle at 50% 46%, rgba(255,250,235,.95) 0%, rgba(255,238,200,.5) 38%, rgba(255,238,200,0) 75%)",
          animation:`bbFlash ${0.6*k}s ease-out forwards`}}/>
      )}
      <div style={{position:"absolute",left:0,right:0,bottom:"calc(var(--vh100) * 0.18)",textAlign:"center",
        fontFamily:"Montserrat,Arial,sans-serif",fontSize:"var(--fs-corps)",fontWeight:700,color:"#fff",
        opacity:phase>=4?1:0,transform:phase>=4?"none":"translateY(10px)",
        transition:`opacity ${0.55*k}s ease ${0.2*k}s, transform ${0.55*k}s ease ${0.2*k}s`}}>
        {prenom?`Bienvenue ${prenom}`:"Bienvenue"} <span aria-hidden="true"><IconeTexte nom="sparkles"/></span>
      </div>
      <div style={{position:"absolute",left:0,right:0,bottom:22,textAlign:"center",fontSize:"var(--fs-micro)",
        color:"rgba(255,255,255,.3)",
        fontFamily:"Montserrat,Arial,sans-serif"}}>Cliquer pour passer</div>
    </div>
  );
};
